import React, { FC } from 'react';
import {
  Notification,
  NotificationList as BPDSNotificationList,
  toSearchParamsString
} from '@beeline.com/bp-design-system';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import { PagePathname } from 'src/utils/hooks/navigation/types';
import { LIST_HEIGHT, StyledEmptyContent } from '../StyledHeaderComponents';
import { useNotificationsIntl } from '../translations/useNotificationsIntl';
import { useNotificationDownload } from './hooks/useNotificationDownload';
import { MappedNotification } from './Notifications.types';
import { NotificationActions } from './NotificationActions';
import {
  checkIsExportType,
  checkIsWorkerQualificationType,
  getExecutionId,
  isNotSupportedExportType
} from './hooks/Notification.utils';
import { useAbsoluteNavigation } from 'src/utils/hooks/navigation/useNavigation';

interface Props {
  unReadCount: number;
  markNotificationsAsRead?: () => Promise<void>;
  notifications: MappedNotification[];
  handleClose?: () => void;
  title?: string;
  removeDismiss?: boolean;
  alwaysUnRead?: boolean;
  source: 'recent-exports' | 'notifications';
}

export const NotificationList: FC<React.PropsWithChildren<Props>> = ({
  handleClose = () => {},
  notifications,
  unReadCount,
  markNotificationsAsRead,
  title,
  removeDismiss,
  alwaysUnRead,
  source
}) => {
  const intl = useNotificationsIntl();
  const onDownload = useNotificationDownload();
  const { track } = useMixpanel();
  const { goToPage } = useAbsoluteNavigation();

  return (
    <BPDSNotificationList
      title={title}
      unReadCount={unReadCount}
      listHeight={LIST_HEIGHT}
      onMarkAllRead={markNotificationsAsRead}
    >
      {!notifications?.length && <StyledEmptyContent>{intl.noNotifications}</StyledEmptyContent>}
      {notifications.map((notification) => {
        const executionUid = getExecutionId(notification);
        const isExportType = checkIsExportType(notification);
        const isWorkerQualificationType = checkIsWorkerQualificationType(notification);
        const notificationTo = notification.to as PagePathname;
        const isNotSupportedExport = isNotSupportedExportType(notification);

        return (
          <Notification
            key={notification.uid}
            uid={notification.uid}
            iconType={notification.iconType}
            title={notification.title}
            read={alwaysUnRead ? false : !!notification.read}
            description={notification.description}
            date={notification.date}
            alwaysShowActions
            dataTest="notification-item"
            clickable={isExportType && !isNotSupportedExport}
            // Manually control redirect to export hub (onClick)
            to={
              // Keep link for other NO export types.
              isNotSupportedExport || isWorkerQualificationType ? notificationTo : undefined
            }
            onClick={() => {
              if (isExportType && !isNotSupportedExport) {
                track(
                  source === 'recent-exports'
                    ? MixpanelEvents.myContentExport.GO_TO_EXPORT_PANEL_FROM_RECENT_EXPORTS
                    : MixpanelEvents.myContentExport.GO_TO_EXPORT_PANEL_FROM_NOTIFICATION
                );
                // Manually control redirect to export hub (onClick)
                // Pass extra state to force redirect in Export Hub (MyContent/Export.tsx)
                goToPage(notificationTo, {
                  state: { executionUid },
                  search: toSearchParamsString({ query: { search: executionUid } })
                });
              }

              handleClose();
            }}
            resourceLocation={notification.resourceLocation}
            onResourceDownload={isNotSupportedExport ? () => onDownload(notification, source) : undefined}
            onDismiss={undefined}
            actions={
              <NotificationActions
                source={source}
                removeDismiss={removeDismiss}
                handleClose={handleClose}
                notification={notification}
              />
            }
          />
        );
      })}
    </BPDSNotificationList>
  );
};
